import { useCallback, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import ReactSignatureCanvas from "react-signature-canvas";
import { CircularProgress, useMediaQuery, Theme } from "@mui/material";
import {
  Accordion,
  Body,
  BottomActionArea,
  BottomActionButton,
  ContentContainer,
  Header,
  Modal,
  NavigateBeforeButton,
  NewChip,
} from "..";
import { api, queryClient, useDoctorContext } from "../../context";
import { DocumentCreationButtonAction, RequestType } from "../../interfaces";
import { useMutation } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import * as S from "./DigitalSign.styles";

interface Props {
  documentType: RequestType;
  handleCreateDocument: () => void;
  handleBack: () => void;
}

type DigitalSignFormValues = {
  signature: string;
  stamp: string;
  useStamp: boolean;
};

export function DigitalSign({
  documentType,
  handleCreateDocument: createDocument,
  handleBack,
}: Props) {
  const doctor = useDoctorContext();

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const signatureCanvasRef = useRef<ReactSignatureCanvas | null>(null);
  const [showDeleteStampModal, setShowDeleteStampModal] = useState(false);
  const [stampJustAdded, setStampJustAdded] = useState(false);

  const { control, handleSubmit, setValue, watch } =
    useForm<DigitalSignFormValues>({
      mode: "onChange",
      defaultValues: {
        signature: doctor.signature ?? "",
        stamp:
          doctor.stamp ??
          `${doctor.name} ${doctor.lastName}\n${doctor.rut}\n${doctor.speciality}\nREG-SIS N°: ${doctor.registrationNumber}`,
        useStamp: doctor.useStamp ?? false,
      },
    });

  const signature = watch("signature");
  const localUseStamp = watch("useStamp");
  const hasStamp = localUseStamp;

  const {
    mutate: updateSignAndCreateDocument,
    isPending,
    isError,
  } = useMutation({
    mutationFn: ({ signature, stamp, useStamp }: DigitalSignFormValues) =>
      api({
        url: `/doctors/${doctor.code}`,
        method: "patch",
        data: { signature, stamp, useStamp },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["doctor"],
      });
      createDocument();
    },
  });

  const handleClear = useCallback(() => {
    signatureCanvasRef.current?.clear();
    setValue("signature", "", { shouldDirty: true, shouldValidate: true });
  }, [setValue]);

  const onSubmit = useCallback(
    (values: DigitalSignFormValues) => {
      const newSignatureDataUrl = signatureCanvasRef.current
        ?.getTrimmedCanvas()
        .toDataURL("image/png");

      const finalSignature = newSignatureDataUrl ?? values.signature ?? "";
      const finalStamp = values.stamp;
      const initialStamp = doctor.stamp ?? "";
      const initialUseStamp = doctor.useStamp ?? false;
      const hasSignatureChange = finalSignature !== (doctor.signature ?? "");
      const hasStampChange =
        values.useStamp !== initialUseStamp || finalStamp !== initialStamp;

      if (newSignatureDataUrl) {
        setValue("signature", newSignatureDataUrl, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }

      if (hasSignatureChange || hasStampChange) {
        updateSignAndCreateDocument({
          signature: finalSignature,
          stamp: finalStamp,
          useStamp: values.useStamp,
        });
      } else {
        createDocument();
      }
    },
    [doctor, setValue, updateSignAndCreateDocument, createDocument],
  );

  const canSubmit = Boolean(signature);

  return (
    <>
      <Header
        title={isMobile ? "Firma digital" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <ContentContainer disableGutters>
        <Body>
          <S.Title variant="h5" fontWeight={"medium"}>
            Firma digital
          </S.Title>
          <S.Container>
            <S.Form onSubmit={handleSubmit(onSubmit)}>
              <S.SignTitle>Dibuja tu firma en el recuadro blanco</S.SignTitle>
              <S.SignatureWrapper>
                <S.ButtonContainer isMobile={isMobile}>
                  <S.CleanSignButton
                    onClick={handleClear}
                    disabled={isPending || !canSubmit}
                  >
                    Limpiar Firma
                  </S.CleanSignButton>
                </S.ButtonContainer>
                <S.SignatureCanvasContainer isMobile={isMobile}>
                  {signature && !signatureCanvasRef.current ? (
                    <S.SignatureImg src={signature} alt="firma digital" />
                  ) : (
                    <SignatureCanvas
                      ref={(instance: ReactSignatureCanvas | null) => {
                        signatureCanvasRef.current = instance;
                      }}
                      onEnd={() => {
                        const dataUrl =
                          signatureCanvasRef.current
                            ?.getTrimmedCanvas()
                            .toDataURL("image/png") ?? "";
                        setValue("signature", dataUrl, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      canvasProps={{
                        style: {
                          width: "100%",
                          height: "100%",
                        },
                      }}
                    />
                  )}
                </S.SignatureCanvasContainer>
                {isError && (
                  <S.ErrorMessage isMobile={isMobile}>
                    Ocurrió un error al firmar el documento, por favor intentalo
                    de nuevo
                  </S.ErrorMessage>
                )}
              </S.SignatureWrapper>
              {hasStamp && isMobile ? (
                <S.MobileStampAccordionContainer>
                  <Accordion
                    expanded={stampJustAdded}
                    title={
                      <S.StampAccordionTitleContainer>
                        <S.StampAccordionTitle>
                          Timbre digital
                        </S.StampAccordionTitle>
                        <NewChip />
                      </S.StampAccordionTitleContainer>
                    }
                  >
                    <S.StampLabel>
                      Al editar la información, presiona Enter para que cada
                      dato quede en su propia línea.{" "}
                    </S.StampLabel>
                    <Controller
                      control={control}
                      name="stamp"
                      render={({ field }) => (
                        <S.StampTextArea
                          {...field}
                          placeholder="Ej: Consultorio, dirección, teléfono..."
                          multiline
                          minRows={3}
                          maxRows={4}
                          variant="outlined"
                          disabled={isPending}
                          helperText="Máximo 4 líneas de texto."
                          onChange={(event) => {
                            const value = event.target.value;
                            const lines = value.split("\n");
                            const limitedValue =
                              lines.length <= 4
                                ? value
                                : lines.slice(0, 4).join("\n");
                            field.onChange(limitedValue);
                          }}
                        />
                      )}
                    />
                    <S.StampDeleteButtonContainer>
                      <S.CleanSignButton
                        onClick={() => setShowDeleteStampModal(true)}
                        disabled={isPending}
                      >
                        Eliminar
                      </S.CleanSignButton>
                    </S.StampDeleteButtonContainer>
                  </Accordion>
                </S.MobileStampAccordionContainer>
              ) : (
                <S.StampWrapper hasStamp={hasStamp}>
                  <S.StampHeader isMobile={isMobile} hasStamp={hasStamp}>
                    <S.StampHeaderTitleContainer>
                      <S.StampTitleText hasStamp={hasStamp} isMobile={isMobile}>
                        Timbre digital
                      </S.StampTitleText>
                      {!hasStamp && <NewChip />}
                    </S.StampHeaderTitleContainer>
                    {hasStamp ? (
                      <S.CleanSignButton
                        onClick={() => setShowDeleteStampModal(true)}
                        disabled={isPending}
                      >
                        Eliminar
                      </S.CleanSignButton>
                    ) : (
                      <S.AddStampButton
                        onClick={() => {
                          setValue("useStamp", true, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                          setStampJustAdded(true);
                        }}
                        disabled={isPending}
                      >
                        + Agregar timbre
                      </S.AddStampButton>
                    )}
                  </S.StampHeader>
                  {hasStamp && (
                    <>
                      <S.StampLabel>
                        Al editar la información, presiona Enter para que cada
                        dato quede en su propia línea.{" "}
                      </S.StampLabel>
                      <Controller
                        control={control}
                        name="stamp"
                        render={({ field }) => (
                          <S.StampTextArea
                            {...field}
                            placeholder="Ej: Consultorio, dirección, teléfono..."
                            multiline
                            minRows={3}
                            maxRows={4}
                            variant="outlined"
                            disabled={isPending}
                            helperText="Máximo 4 líneas de texto."
                            onChange={(event) => {
                              const value = event.target.value;
                              const lines = value.split("\n");
                              const limitedValue =
                                lines.length <= 4
                                  ? value
                                  : lines.slice(0, 4).join("\n");
                              field.onChange(limitedValue);
                            }}
                          />
                        )}
                      />
                    </>
                  )}
                </S.StampWrapper>
              )}
              <BottomActionArea>
                <BottomActionButton
                  type="submit"
                  disabled={isPending || !canSubmit}
                >
                  {isPending ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    DocumentCreationButtonAction[documentType]
                  )}
                </BottomActionButton>
              </BottomActionArea>
              <Modal
                title="Eliminar timbre"
                content="¿Estás seguro que quieres eliminar el timbre?"
                isOpen={showDeleteStampModal}
                onClose={() => setShowDeleteStampModal(false)}
                buttonProps={{
                  buttonText: "Eliminar",
                  onClick: () => {
                    setValue("useStamp", false, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setShowDeleteStampModal(false);
                  },
                }}
                secondaryButtonProps={{
                  buttonText: "Cancelar",
                  onClick: () => setShowDeleteStampModal(false),
                }}
              />
            </S.Form>
          </S.Container>
        </Body>
      </ContentContainer>
    </>
  );
}
